import { ColorTypes, ComponentAdapter, utils, actions } from "@omegagrid/core";
import { HistoryItem, ToolbarItemOptions } from "../types";
import { CodeEditor } from "./codeEditor";
import { TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import { CodeCompletionProvider, CodeLinkProvider } from "@reporting/rp-utils-sql-completion";

export type CodeEditorHistoryOptions = {
	enabled?: boolean,
	placeholder?: string
}

export type CodeEditorToolbarOptions = {
	save?: ToolbarItemOptions,
	discard?: ToolbarItemOptions,
	refresh?: ToolbarItemOptions,
	[key: string]: ToolbarItemOptions
}

export type CodeEditorOptions = {
	readOnly?: boolean,
	pageSize?: number,
	language?: string,
	history?: boolean | CodeEditorHistoryOptions,
	toolbar?: CodeEditorToolbarOptions
}

const DEFAULT_CODE_EDITOR_OPTIONS: CodeEditorOptions = {
	toolbar: {
		save: {
			id: 'save',
			type: 'button',
			text: 'Save changes',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true,
			ord: 1
		},
		discard: {
			id: 'discard',
			type: 'button',
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true,
			ord: 2
		}
	},
}

export type ContentOptions = {
	offset?: number,
	limit?: number
};

export abstract class CodeEditorAdapter<TEditor extends CodeEditor = CodeEditor> extends ComponentAdapter<TEditor> {

	abstract getContent(opt?: ContentOptions) : string|Promise<string>;

	getHistoryContent(idz: number, opt?: ContentOptions) : string|Promise<string> {
		return null;
	}

	getUri(): string | Promise<string> {
		return null;
	}

	save(content: string) : void|boolean|Promise<void|boolean> {};

	getOptions(): null|Partial<CodeEditorOptions>|Promise<Partial<CodeEditorOptions>> { return DEFAULT_CODE_EDITOR_OPTIONS }

	getHistoryItems() : HistoryItem[]|Promise<HistoryItem[]> {
		return [];
	}

	getCurrentItem() : HistoryItem|Promise<HistoryItem> {
		return null;
	}

	createOptions(options: Partial<CodeEditorOptions>): CodeEditorOptions {
		return utils.mergeDeep(DEFAULT_CODE_EDITOR_OPTIONS, options);
	}

	onAction(action: actions.Action) { }

	/**
	 * Completion (autocomplete) offered in the editor; null keeps only Monaco's built-in suggestions. SQL editors
	 * get the shared T-SQL provider by default (see SqlEditorAdapter and createSqlCompletionProvider()); other
	 * adapters return their own provider or combine several with mergeCompletionProviders().
	 */
	getCompletionProvider(): null | CodeCompletionProvider | Promise<null | CodeCompletionProvider> {
		return null;
	}

	/**
	 * Links (Ctrl+click navigation) offered in the editor; null means no links. Adapters return a provider
	 * for the places of their document that lead somewhere, e.g. createSqlIncludeLinkProvider() for the
	 * `{{include}}` macros of ETL threads, or combine several with mergeLinkProviders().
	 */
	getLinkProvider(): null | CodeLinkProvider | Promise<null | CodeLinkProvider> {
		return null;
	}

}

export class CodeHistoryAdapter extends TreeAdapter {

	private items: TreeSource = null;

	constructor(private adapter: CodeEditorAdapter<CodeEditor>) {
		super();
	}

	invalidateItems() {
		this.items = null;
	}

	formatItem(item: HistoryItem): string {
		if (!item.date_created) return '';
		return `${item.date_created.substring(0, 19)} - ${item.who_created_dv ?? item.who_created ?? '<unknown>'}`;
	}

	async getTree(): Promise<TreeSource> {
		if (this.items === null) {
			const items = await this.adapter.getHistoryItems();
			this.items = items.map(item => ({
				k: item.idz.toString(),
				v: this.formatItem(item),
			}));
		}
		return this.items;
	}

    getOptions(): Partial<TreeOptions> {
		return {
			selectMode: 'single',
			autoClose: true,
		};
	}

}
